import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { legalTranslations } from "@/i18n/legalTranslations";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "ck-cookie-consent";

type CookiePreferences = {
  technical: boolean;
  analytical: boolean;
  advertising: boolean;
  timestamp: number;
};

const getStoredPreferences = (): CookiePreferences | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const savePreferences = (prefs: CookiePreferences) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
};

const CookieConsent = () => {
  const { lang } = useLanguage();
  const lt = legalTranslations[lang];
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytical, setAnalytical] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    const prefs = getStoredPreferences();
    if (!prefs) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = useCallback(() => {
    savePreferences({ technical: true, analytical: true, advertising: true, timestamp: Date.now() });
    setVisible(false);
    setShowSettings(false);
  }, []);

  const reject = useCallback(() => {
    savePreferences({ technical: true, analytical: false, advertising: false, timestamp: Date.now() });
    setVisible(false);
    setShowSettings(false);
  }, []);

  const saveCustom = useCallback(() => {
    savePreferences({ technical: true, analytical, advertising, timestamp: Date.now() });
    setVisible(false);
    setShowSettings(false);
  }, [analytical, advertising]);

  // Allow reopening from footer
  useEffect(() => {
    const handler = () => {
      const prefs = getStoredPreferences();
      if (prefs) {
        setAnalytical(prefs.analytical);
        setAdvertising(prefs.advertising);
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  return (
    <AnimatePresence>
      {visible && !showSettings && (
        <motion.div
          key="banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[90] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-kuro/95 backdrop-blur-lg border border-shiro/10 rounded-lg p-5 md:p-6 shadow-2xl">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-aka shrink-0 mt-0.5 hidden sm:block" />
              <div className="flex-1">
                <h3 className="font-display text-lg text-shiro mb-2">{lt.cookie_banner_title}</h3>
                <p className="text-shiro/60 text-sm font-body leading-relaxed mb-4">{lt.cookie_banner_desc}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={accept}
                    className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-6 py-2.5 hover:bg-primary/90 transition-colors font-body"
                  >
                    {lt.cookie_accept_all}
                  </button>
                  <button
                    onClick={reject}
                    className="border border-shiro/20 text-shiro text-xs tracking-widest uppercase px-6 py-2.5 hover:bg-shiro/10 transition-colors font-body"
                  >
                    {lt.cookie_reject}
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="text-shiro/60 text-xs tracking-widest uppercase px-6 py-2.5 hover:text-shiro transition-colors font-body underline underline-offset-4"
                  >
                    {lt.cookie_configure}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {visible && showSettings && (
        <motion.div
          key="settings"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => { setShowSettings(false); setVisible(false); }}
        >
          <div className="absolute inset-0 bg-kuro/70 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 w-full max-w-lg bg-background rounded-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="font-display text-xl">{lt.cookie_settings_title}</h3>
              <button onClick={() => { setShowSettings(false); setVisible(false); }} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Technical - always on */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-body font-medium text-sm">{lt.cookie_technical}</p>
                  <p className="text-muted-foreground text-xs font-body mt-1 leading-relaxed">{lt.cookie_technical_desc}</p>
                </div>
                <span className="text-xs text-aka font-body shrink-0 mt-1">{lt.cookie_always_active}</span>
              </div>

              {/* Analytical */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-body font-medium text-sm">{lt.cookie_analytical}</p>
                  <p className="text-muted-foreground text-xs font-body mt-1 leading-relaxed">{lt.cookie_analytical_desc}</p>
                </div>
                <Switch checked={analytical} onCheckedChange={setAnalytical} />
              </div>

              {/* Advertising */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-body font-medium text-sm">{lt.cookie_advertising}</p>
                  <p className="text-muted-foreground text-xs font-body mt-1 leading-relaxed">{lt.cookie_advertising_desc}</p>
                </div>
                <Switch checked={advertising} onCheckedChange={setAdvertising} />
              </div>
            </div>

            <div className="p-5 border-t border-border flex flex-col sm:flex-row gap-3">
              <button
                onClick={saveCustom}
                className="flex-1 bg-primary text-primary-foreground text-xs tracking-widest uppercase px-6 py-2.5 hover:bg-primary/90 transition-colors font-body"
              >
                {lt.cookie_save}
              </button>
              <button
                onClick={accept}
                className="flex-1 border border-border text-foreground text-xs tracking-widest uppercase px-6 py-2.5 hover:bg-muted transition-colors font-body"
              >
                {lt.cookie_accept_all}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
